import { Autocomplete, Box, IconButton, Stack, TextField } from '@mui/material'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/Add'
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/Remove'
import { DeckMappDialog } from '../../../../components/DeckMappDialog'

export const OtherKitsMaintenance = ({ otherKits, onClose, onSubmit, isMobile }) => {
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { isValid }
  } = useForm({
    defaultValues: {
      kits: [
        {
          materialNumber: '',
          quantity: 0
        }
      ]
    },
    mode: 'onChange'
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'kits'
  })

  // Watch all kit selections to filter out already-selected kits
  const allKits = watch('kits')

  // Get available kits for a specific row (exclude already selected kits except current row's selection)
  const getAvailableKitsForRow = (currentIndex) => {
    const selectedMaterialNumbers = allKits
      .map((kit, index) => (index !== currentIndex ? kit.materialNumber : null))
      .filter(Boolean)

    return (otherKits || []).filter((kit) => !selectedMaterialNumbers.includes(kit.MaterialNumber))
  }

  return (
    <DeckMappDialog
      open
      subtitle="Please select the kits and specify the quantities replaced during maintenance."
      title="Other Kits Maintenance"
      onCancel={onClose}
      isMobile={isMobile}
      saveButtonProps={{
        negativeButtonTitle: 'Cancel',
        title: 'Save',
        form: 'form',
        type: 'submit',
        disabled: !isValid
      }}>
      <form
        id="form"
        onSubmit={handleSubmit((data) => {
          onSubmit(data.kits)
        })}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1em', justifyContent: 'center' }}>
          {fields.map((field, i) => (
            <Stack
              key={field.id}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              direction="row"
              sx={{
                marginBottom: '.6em !important'
              }}
              mb={2}>
              <Controller
                control={control}
                name={`kits.${i}.materialNumber`}
                rules={{ required: 'Select a kit' }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={getAvailableKitsForRow(i)}
                    value={
                      field.value
                        ? (otherKits || []).find((kit) => kit.MaterialNumber === field.value) || field.value
                        : null
                    }
                    sx={{ width: '40%' }}
                    multiple={false}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof option === 'string') return option === value
                      if (typeof value === 'string') return option.MaterialNumber === value
                      return option.MaterialNumber === value?.MaterialNumber
                    }}
                    getOptionKey={(option) => (typeof option === 'string' ? option : option.MaterialNumber)}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option
                      const description = option.MaterialDescription
                      return description ? `${option.MaterialNumber} - ${description}` : option.MaterialNumber
                    }}
                    onChange={(_, value) => {
                      field.onChange(value ? value.MaterialNumber : '')
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select kit"
                        variant="standard"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                control={control}
                name={`kits.${i}.quantity`}
                rules={{
                  required: 'Quantity is required',
                  validate: (value) => {
                    const num = Number(value)
                    if (Number.isNaN(num)) return 'Must be a number'
                    if (num <= 0) return 'Quantity must be greater than 0'
                    const selected = getValues(`kits.${i}.materialNumber`)
                    if (!selected) return 'Select a kit first'
                    const kit = (otherKits || []).find((k) => k.MaterialNumber === selected)
                    if (!kit) return true
                    // Try common fields for available quantity
                    const max = kit.Quantity
                    if (max == null) return true
                    return num <= Number(max) || `Must be less than or equal to available quantity (${max})`
                  }
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Quantity"
                    placeholder="e.g. 10"
                    color="secondary"
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    sx={{ width: '40%', marginTop: '5px' }}
                    inputProps={{ min: 1 }}
                  />
                )}
              />
              <IconButton color="secondary" variant="text" onClick={() => remove(i)}>
                <RemoveCircleOutlineOutlinedIcon />
              </IconButton>
            </Stack>
          ))}

          <Box>
            {fields.length < otherKits.length && (
              <IconButton
                color="secondary"
                variant="text"
                onClick={() =>
                  append({
                    kit: '',
                    quantity: 0
                  })
                }>
                <AddCircleOutlineOutlinedIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </form>
    </DeckMappDialog>
  )
}
